import agentStore from '@/stores/agent';
import React, { ChangeEvent } from 'react';
import { getNetWorkInfo } from './getNetWorkInfo';
import { bridgeCallFunc, getFileExtension, getTrans } from './instantLoaded/tools';
import { Copywriting, NONE_WIFI_MAX_FILE_SIZE, NONE_WIFI_UPLOAD_CANCEL, NONE_WIFI_UPLOAD_CONFIRM, isIosReg, MediaType, MediaConstants, PicAndVideoLimit, MediaSourceType } from './constants';
import { getImageFileFromBase64, getNewMediaUploadVersion, replaceParams } from './tools';
import ConfirmModal from '@/components/General/ConfirmModal';
import { getConfig, isBank } from '@/config/core';
import { enableIosBlankDetection } from './agent';
import { toast } from '@shopee/chatbot-ui';

export const SUPPORT_FILE_TYPE = ['.csv', '.xls', '.xlsx', '.xlsm', '.docx', '.doc', '.pdf'];

const showOverSizeToast = (transKey: string, params = {}) => {
  toast.error(replaceParams(getTrans(transKey), params) as string);
};

export async function handleSelectFile(e: ChangeEvent<HTMLInputElement>, callback: (file: File) => void) {
  const files = e.target.files || [];
  const file = files[0];
  const { maxFileSize } = agentStore;
  const { networkType } = await getNetWorkInfo();
  const fileExtension = getFileExtension(file.name)?.toLowerCase();

  // 如果文件类型不合法
  if (!SUPPORT_FILE_TYPE.includes(`.${fileExtension}`)) {
    toast.error(getTrans(Copywriting.FILE_TYPE_UNSUPPORTED));
    return;
  }

  // 如果文件太大 如果以后传多个也只出现一次
  if (file.size > maxFileSize) {
    showOverSizeToast(Copywriting.FILE_UPLOAD_OVERSIZE, { size: maxFileSize / 1024 / 1024 });
    return;
  }

  // 非WiFI且文件大于10M
  if (networkType !== 1 && file.size > NONE_WIFI_MAX_FILE_SIZE) {
    // 转为MB
    const fileSize = (file.size / 1024 / 1024).toFixed(1);
    ConfirmModal({
      className: 'file-confirm-modal',
      title: '',
      content: <div className="content">{replaceParams(getTrans('file_upload_non_wifi'), { size: fileSize })}</div>,
      footer: [
        { value: getTrans(NONE_WIFI_UPLOAD_CANCEL) },
        {
          value: getTrans(NONE_WIFI_UPLOAD_CONFIRM),
          type: 'primary',
        },
      ],
      onFooterClick: ({ value }) => {
        if (value === getTrans(NONE_WIFI_UPLOAD_CONFIRM)) {
          callback(file);
          return;
        }
      },
    });
    return;
  }

  callback(file);
}

export function handleSelectPhotoLibrary(photoHandler: (images: File[]) => void, galleryHandler: (data: any) => void) {
  if (useNewMediaEntry()) {
    handleGalleryBtn(MediaSourceType.Album, galleryHandler);
  } else {
    handleSelectPhotos(photoHandler);
  }
}

const { userAgent } = window.navigator;
const isIos = isIosReg.test(userAgent);
export function handleSelectPhotos(callback: (images: File[]) => void) {
  const params = isIos ? {
    type: 2,
    maxCount: 9,
  } : {
    width: 1024,
    height: 1024,
    type: 2,
    maxCount: 9,
  };

  bridgeCallFunc(() => {
    window.bridgeCallHandler('pickImage', params, (data: any) => {
      const { images = [], image } = data;
      const { maxImageSize } = agentStore;

      // 只选了一张图片时，数据从image返回而不是images
      if (images.length === 0 && image) {
        images.push(image);
      }

      const isOversize = (img: any) => {
        const currentImage = getImageFileFromBase64(img);
        return currentImage?.size > maxImageSize;
      };

      const legalImages = images.filter((img: any) => !isOversize(img));
      if (legalImages.length !== 0) {
        callback(legalImages);
      }
      // 如果有超出大小的 就toast提示
      // 是否有图片超过大小
      const hasImageOversize = images.some(isOversize);
      if (hasImageOversize) {
        showOverSizeToast(Copywriting.IMAGE_UPLOAD_OVERSIZE, { size: maxImageSize / 1024 / 1024 });
      }
    });
  });
}

export function handleGallerySelect(source: number, callback: (data: any, newMediaEntry: boolean) => void) {
  if (useNewMediaEntry()) {
    handleGalleryBtn(source, (data) => callback(data, true));
  } else {
    handleCameraBtn((data) => callback(data, false));
  }
}

export const handleCameraBtn = (callback: (data: any) => void) => {
  const params = isIos ? {
    type: 1,
    maxCount: 1,
  } : {
    width: 1024,
    height: 1024,
    type: 1,
    maxCount: 1,
  };

  bridgeCallFunc(() => {
    window.bridgeCallHandler('pickImage', params, (data: any) => {
      callback(data.image);
    });
  });
};


export const handleGalleryBtn = (source: number, callback: (data: any) => void) => {
  const params = {
    source,
    mediaType: isBank() ? MediaType.Image : MediaType.ImageAndVideo,
    bizIdForMediaSDK: MediaConstants.MediaBizId,
    videoMiniDuration: PicAndVideoLimit.VideoMinDuration,
    videoMaxDuration: PicAndVideoLimit.VideoMaxDuration,
    videoMaxSize: PicAndVideoLimit.VideoMaxSize,
    maxSelectCount: PicAndVideoLimit.Count,
    videoCoverMaxWidthOrHeight: PicAndVideoLimit.VideoCoverMaxWidthOrHeight,
    videoCoverCompressionQuality: PicAndVideoLimit.VideoCoverCompressionQuality,
  };

  bridgeCallFunc(() => {
    enableIosBlankDetection(false);
    window.bridgeCallHandler('mediaSelect', params, ({ data, error }: {data: any; error: any}) => {
      if (error === 0) {
        callback(data);
      }
      enableIosBlankDetection(true);
    });
  });
};

const useNewMediaEntry = () => {
  const config = getConfig();
  // 需要隐藏video上传入口的地区
  const hideNewMediaCountries = config.hideNewMediaCountries || [];

  if (hideNewMediaCountries.includes(COUNTRY)) {
    return false;
  }

  // 2.73 之后才使用新的
  const version = window.getAppVersion();
  if (version >= getNewMediaUploadVersion()) {
    return true;
  }

  return false;
};
